import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FileText, Loader2, Check, Plus, Trash2, Upload, Pencil, Eye, EyeOff } from "lucide-react";
import { API } from "../data/site";

const inputCls = "w-full border border-slate-300 bg-white px-3 py-2 text-sm text-navy focus:border-safety focus:outline-none";
const labelCls = "font-mono text-[9px] uppercase tracking-[0.18em] text-slate-500";

const slugify = (t) =>
  t.toLowerCase().replace(/&/g, "and").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 90);

const emptyPost = {
  slug: "", title: "", excerpt: "", tag: "Technical Guide", date: new Date().toISOString().slice(0, 10),
  readTime: "6 min read", image: "", seoDescription: "", keywords: "", content: "", published: true,
};

const PostEditor = ({ post, onDone }) => {
  const isNew = !post;
  const qc = useQueryClient();
  const [form, setForm] = useState(
    post ? { ...post, published: post.published !== false } : emptyPost
  );
  const [imgFile, setImgFile] = useState(null);
  const [err, setErr] = useState("");
  const imgRef = useRef(null);

  useEffect(() => {
    setForm(post ? { ...post, published: post.published !== false } : emptyPost);
  }, [post]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: async () => {
      const payload = { ...form, slug: form.slug || slugify(form.title) };
      const res = await fetch(isNew ? `${API}/admin/posts` : `${API}/admin/posts/${post.slug}`, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Save failed");
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["posts"] });
      onDone();
    },
    onError: (e) => setErr(e.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API}/admin/posts/${post.slug}`, { method: "DELETE", credentials: "include" });
      if (!res.ok) throw new Error("Delete failed");
      return res.json();
    },
    onSuccess: onDone,
  });

  const uploadImage = useMutation({
    mutationFn: async () => {
      const fd = new FormData();
      fd.append("file", imgFile);
      const res = await fetch(`${API}/admin/posts/image`, { method: "POST", credentials: "include", body: fd });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      return data;
    },
    onSuccess: (data) => {
      setForm((f) => ({ ...f, image: data.url }));
      setImgFile(null);
      if (imgRef.current) imgRef.current.value = "";
    },
    onError: (e) => setErr(e.message),
  });

  return (
    <div className="border border-slate-200 bg-white p-6 sm:p-8" data-testid="blog-editor">
      <div className="flex items-center justify-between gap-4">
        <h2 className="flex items-center gap-2.5 font-display text-lg font-extrabold text-navy">
          <Pencil size={16} className="text-safety" /> {isNew ? "New article" : "Edit article"}
        </h2>
        <button onClick={onDone} data-testid="blog-editor-close" className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500 hover:text-safety">Close</button>
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-[300px_1fr]">
        <div className="space-y-4">
          <div className="overflow-hidden border border-slate-200 bg-navy" data-testid="blog-image-preview">
            {form.image ? (
              <img src={form.image} alt="" className="aspect-[16/9] w-full object-cover" />
            ) : (
              <div className="flex aspect-[16/9] items-center justify-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">No image</div>
            )}
          </div>
          <label className="block">
            <span className={labelCls}>Cover image URL</span>
            <input value={form.image} onChange={set("image")} data-testid="blog-image-url-input" className={`${inputCls} mt-1`} placeholder="/img/… or https://…" />
          </label>
          <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-slate-300 bg-slate-50 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-navy hover:border-safety hover:text-safety">
            <Upload size={13} className="text-safety" /> Upload cover
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" ref={imgRef}
              onChange={(e) => setImgFile(e.target.files?.[0] || null)} data-testid="blog-image-file-input" />
          </label>
          {imgFile && (
            <button onClick={() => uploadImage.mutate()} disabled={uploadImage.isPending}
              data-testid="blog-image-upload-button"
              className="flex w-full items-center justify-center gap-2 bg-safety px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-white disabled:opacity-60">
              {uploadImage.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Upload image
            </button>
          )}
        </div>

        <div className="space-y-4">
          <label className="block"><span className={labelCls}>Title</span>
            <input value={form.title} onChange={set("title")} data-testid="blog-title-input" className={`${inputCls} mt-1 font-display font-bold`} /></label>
          <div className="grid gap-4 sm:grid-cols-3">
            <label className="block"><span className={labelCls}>Slug (URL)</span>
              <input value={form.slug} onChange={set("slug")} data-testid="blog-slug-input" placeholder={slugify(form.title) || "auto"} className={`${inputCls} mt-1 font-mono text-xs`} /></label>
            <label className="block"><span className={labelCls}>Date</span>
              <input type="date" value={form.date} onChange={set("date")} data-testid="blog-date-input" className={`${inputCls} mt-1`} /></label>
            <label className="block"><span className={labelCls}>Read time</span>
              <input value={form.readTime} onChange={set("readTime")} data-testid="blog-readtime-input" className={`${inputCls} mt-1`} /></label>
          </div>
          <label className="block"><span className={labelCls}>Excerpt (card preview)</span>
            <textarea rows={2} value={form.excerpt} onChange={set("excerpt")} data-testid="blog-excerpt-input" className={`${inputCls} mt-1`} /></label>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block"><span className={labelCls}>Tag</span>
              <input value={form.tag} onChange={set("tag")} data-testid="blog-tag-input" className={`${inputCls} mt-1`} /></label>
            <label className="block"><span className={labelCls}>SEO keywords</span>
              <input value={form.keywords} onChange={set("keywords")} data-testid="blog-keywords-input" className={`${inputCls} mt-1`} /></label>
          </div>
          <label className="block"><span className={labelCls}>SEO description</span>
            <textarea rows={2} value={form.seoDescription} onChange={set("seoDescription")} data-testid="blog-seo-input" className={`${inputCls} mt-1`} /></label>

          <label className="block">
            <span className={labelCls}>Article content</span>
            <textarea rows={18} value={form.content} onChange={set("content")} data-testid="blog-content-input"
              className={`${inputCls} mt-1 font-mono text-xs leading-relaxed`}
              placeholder={"Paragraph text. Wrap **bold** with asterisks.\n\n## Section heading\n### Sub heading\n- Bullet point\n|Column 1|Column 2|  ← table rows, first row is the header"} />
          </label>
          <p className="font-mono text-[9px] uppercase tracking-[0.16em] text-slate-400">
            Format: blank line = new paragraph · "## " heading · "### " sub-heading · "- " bullet · "|a|b|c|" table rows (first row = header) · **bold**
          </p>

          <label className="flex items-center gap-3" data-testid="blog-published-toggle">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm((f) => ({ ...f, published: e.target.checked }))} className="h-4 w-4 accent-[#FF5500]" />
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-navy">
              {form.published ? <Eye size={13} className="text-safety" /> : <EyeOff size={13} className="text-slate-400" />}
              {form.published ? "Published — visible on site" : "Draft — hidden from site"}
            </span>
          </label>

          {err && <p className="border-l-2 border-red-500 bg-red-50 px-4 py-2.5 text-xs text-red-700">{err}</p>}

          <div className="flex flex-wrap gap-3">
            <button onClick={() => { setErr(""); save.mutate(); }} disabled={save.isPending}
              data-testid="blog-save-button"
              className="flex items-center gap-2.5 bg-safety px-7 py-3.5 font-display text-sm font-bold uppercase tracking-[0.12em] text-white transition-colors hover:bg-navy disabled:opacity-60">
              {save.isPending ? <><Loader2 size={15} className="animate-spin" /> Saving…</> : <><Check size={15} /> {isNew ? "Publish article" : "Save changes"}</>}
            </button>
            {!isNew && (
              <button
                onClick={() => { if (window.confirm("Delete this article permanently?")) remove.mutate(); }}
                disabled={remove.isPending}
                data-testid="blog-delete-button"
                className="flex items-center gap-2 border border-red-300 px-5 py-3.5 font-mono text-[10px] uppercase tracking-[0.16em] text-red-600 transition-colors hover:bg-red-50 disabled:opacity-60"
              >
                <Trash2 size={13} /> Delete
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const AdminBlog = () => {
  const [editing, setEditing] = useState(undefined); // undefined = list view, null = new, object = edit
  const { data, isLoading } = useQuery({
    queryKey: ["posts", true],
    queryFn: async () => {
      const res = await fetch(`${API}/admin/posts`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
  });
  const posts = data?.posts || [];

  return (
    <div data-testid="admin-blog-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight text-navy">Blog</h1>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500">
            Write, publish and manage technical articles
          </p>
        </div>
        {editing === undefined && (
          <button
            onClick={() => setEditing(null)}
            data-testid="admin-new-post-button"
            className="flex items-center gap-2 bg-safety px-5 py-3 font-display text-xs font-bold uppercase tracking-[0.14em] text-white transition-colors hover:bg-navy"
          >
            <Plus size={14} /> New article
          </button>
        )}
      </div>

      {editing !== undefined ? (
        <div className="mt-6">
          <PostEditor post={editing || null} onDone={() => setEditing(undefined)} />
        </div>
      ) : isLoading ? (
        <p className="mt-10 font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Loading articles…</p>
      ) : (
        <div className="mt-6 space-y-4" data-testid="admin-blog-list">
          {posts.map((p) => (
            <div key={p.slug} className="flex items-center gap-4 border border-slate-200 bg-white p-4">
              <img src={p.image} alt="" className="h-14 w-24 shrink-0 object-cover" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-display text-sm font-bold text-navy">{p.title}</p>
                <p className="mt-0.5 flex items-center gap-3 font-mono text-[9px] uppercase tracking-[0.16em] text-slate-500">
                  <span>{new Date(p.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                  <span>/{p.slug}</span>
                  {p.published !== false ? (
                    <span className="bg-emerald-50 px-2 py-0.5 text-emerald-700">Published</span>
                  ) : (
                    <span className="bg-slate-100 px-2 py-0.5 text-slate-500">Draft</span>
                  )}
                </p>
              </div>
              <button
                onClick={() => setEditing(p)}
                data-testid={`admin-blog-edit-${p.slug}`}
                className="flex shrink-0 items-center gap-2 border border-slate-300 px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.16em] text-navy transition-colors hover:border-safety hover:text-safety"
              >
                <Pencil size={12} /> Edit
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminBlog;
